import React, { useState } from 'react';
import { Box, Paper, List, ListItem, ListItemText, Typography, IconButton, Button, TextField } from '@mui/material';
import { ArrowForward, ArrowBack, Search } from '@mui/icons-material';

const ColumnSelector = ({ availableColumns, selectedColumns, onSelectedColumnsChange }) => {

  const [availableFilter, setAvailableFilter] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');

  const handleDoubleClickAvailable = (column) => {
    const newSelectedColumns = [...selectedColumns, column];
    onSelectedColumnsChange(newSelectedColumns);
  };

  const handleDoubleClickSelected = (column) => {
    const newSelectedColumns = selectedColumns.filter(c => c !== column);
    onSelectedColumnsChange(newSelectedColumns);
  };

  const handleAddAll = () => {
    const updatedColumns = [...selectedColumns, ...filteredAvailableColumns];
    onSelectedColumnsChange(updatedColumns);
  };

  const handleRemoveAll = () => {
    if (!selectedFilter) {
      // If no filter is applied, remove all columns
      onSelectedColumnsChange([]);
    } else {
      // If filter is applied, only remove the filtered columns
      const columnsToKeep = selectedColumns.filter(
        col => !col.toLowerCase().includes(selectedFilter.toLowerCase())
      );
      console.log('After removing all:', columnsToKeep);
      onSelectedColumnsChange(columnsToKeep);
    }
  };

  // Filter columns based on search input
  const filteredAvailableColumns = availableColumns.filter(col => 
    !selectedColumns.includes(col) && 
    col.toLowerCase().includes(availableFilter.toLowerCase())
  );

  const filteredSelectedColumns = selectedColumns.filter(col =>
    col.toLowerCase().includes(selectedFilter.toLowerCase())
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
      {/* Available Columns */}
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Available Columns</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search columns..."
          value={availableFilter}
          onChange={(e) => setAvailableFilter(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
          sx={{ mb: 1 }}
        />
        <Paper variant="outlined" sx={{ height: 400, overflow: 'auto' }}>
          <List dense>
            {filteredAvailableColumns.map((column) => (
              <ListItem
                key={column}
                onDoubleClick={() => handleDoubleClickAvailable(column)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemText primary={column} />
              </ListItem>
            ))}
            {filteredAvailableColumns.length === 0 && (
              <ListItem>
                <ListItemText 
                  primary="No matching columns" 
                  sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
        <Button 
          variant="outlined" 
          onClick={handleAddAll}
          disabled={filteredAvailableColumns.length === 0}
        >
          {availableFilter ? 'Add Filtered →' : 'Add All →'}
        </Button>
        <Button 
          variant="outlined" 
          onClick={handleRemoveAll}
          disabled={selectedFilter ? filteredSelectedColumns.length === 0 : selectedColumns.length === 0}
        >
          {selectedFilter ? '← Remove Filtered' : '← Remove All'}
        </Button>
      </Box>

      {/* Selected Columns */}
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Selected Columns</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search columns..."
          value={selectedFilter}
          onChange={(e) => setSelectedFilter(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
          sx={{ mb: 1 }}
        />
        <Paper variant="outlined" sx={{ height: 400, overflow: 'auto' }}>
          <List dense>
            {filteredSelectedColumns.map((column) => (
              <ListItem
                key={column}
                onDoubleClick={() => handleDoubleClickSelected(column)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemText primary={column} />
              </ListItem>
            ))}
            {filteredSelectedColumns.length === 0 && (
              <ListItem>
                <ListItemText 
                  primary="No matching columns" 
                  sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};

export default ColumnSelector; 